class LibertasProduct extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    this.variants = JSON.parse(this.querySelector('[data-product-variants]').textContent);
    const inventoryData = this.querySelector('[data-product-inventory]');
    this.inventoryByVariant = new Map(
      (inventoryData ? JSON.parse(inventoryData.textContent) : []).map((item) => [String(item.id), item])
    );
    this.photoSwipe = null;
    this.photoSwipePromise = null;
    this.initializeMobileStickyPurchase();
    this.addEventListener('click', (event) => {
      const sizeGuideOpen = event.target.closest('[data-size-guide-open]');
      if (sizeGuideOpen) {
        this.openSizeGuide(sizeGuideOpen);
        return;
      }
      const sizeGuideClose = event.target.closest('[data-size-guide-close]');
      if (sizeGuideClose) {
        this.closeSizeGuide();
        return;
      }
      const descriptionOpen = event.target.closest('[data-description-open]');
      if (descriptionOpen) {
        this.openDescription();
        return;
      }
      const backInStockOpen = event.target.closest('[data-back-in-stock-open]');
      if (backInStockOpen) {
        this.openBackInStock(backInStockOpen);
        return;
      }
      const backInStockClose = event.target.closest('[data-back-in-stock-close]');
      if (backInStockClose) {
        this.closeBackInStock();
        return;
      }
      const productTab = event.target.closest('[data-product-tab]');
      if (productTab) {
        this.activateProductTab(productTab);
        return;
      }
      const unitButton = event.target.closest('[data-size-unit]');
      if (unitButton) {
        this.setSizeUnit(unitButton.dataset.sizeUnit);
        return;
      }
      const zoomTrigger = event.target.closest('[data-product-zoom]');
      if (zoomTrigger) {
        event.preventDefault();
        this.openGalleryZoom(zoomTrigger);
        return;
      }
      const quantityButton = event.target.closest('[data-quantity-change]');
      if (!quantityButton) return;
      this.changeQuantity(quantityButton.dataset.quantityChange);
    });
    this.addEventListener('change', (event) => {
      if (event.target.matches('[name^="options"]')) {
        this.updateVariant();
      } else if (event.target.matches('[data-quantity-input]')) {
        this.normalizeQuantity();
      }
    });
    this.addEventListener('submit', (event) => {
      if (event.target.matches('[data-back-in-stock-form]')) {
        event.preventDefault();
        this.submitBackInStock(event.target);
        return;
      }
      this.normalizeQuantity();
    });
    this.addEventListener('keydown', (event) => this.handleProductTabKeydown(event));
    this.querySelectorAll('[data-media-target]').forEach((button) => {
      button.addEventListener('click', () => this.showMedia(button.dataset.mediaTarget));
    });
    this.initializeGallery();
    const sizeDialog = this.querySelector('[data-size-guide-dialog]');
    if (sizeDialog) {
      sizeDialog.addEventListener('click', (event) => {
        if (event.target === sizeDialog) this.closeSizeGuide();
      });
      sizeDialog.addEventListener('close', () => this.sizeGuideTrigger?.focus());
    }
    this.initializeBackInStock();
    this.syncQuantityButtons();
    this.updateOptionAvailability();
    const selectedVariantId = this.querySelector('.lib-product-form [name="id"]')?.value;
    const selectedVariant = this.variants.find((variant) => String(variant.id) === selectedVariantId);
    if (selectedVariant) {
      this.updatePrice(selectedVariant);
      this.updatePayPalMessage(selectedVariant);
      this.updatePurchaseAvailability(selectedVariant);
    }
  }

  disconnectedCallback() {
    this.photoSwipe?.destroy();
    this.photoSwipe = null;
    this.photoSwipePromise = null;
    this.mobileStickyMediaQuery?.removeEventListener('change', this.mobileStickyMediaHandler);
    this.mobileStickyResizeObserver?.disconnect();
    this.restoreMobileStickyPurchase();
    document.body.classList.remove('lib-mobile-product-sticky-active');
    document.documentElement.style.removeProperty('--lib-mobile-product-sticky-height');
  }

  initializeMobileStickyPurchase() {
    this.mobileStickyPurchase = this.querySelector('[data-mobile-sticky-purchase]');
    if (!this.mobileStickyPurchase) return;

    const slots = {
      title: this.mobileStickyPurchase.querySelector('[data-mobile-sticky-slot="header"]'),
      price: this.mobileStickyPurchase.querySelector('[data-mobile-sticky-slot="header"]'),
      variants: this.mobileStickyPurchase.querySelector('[data-mobile-sticky-slot="variants"]'),
      buy: this.mobileStickyPurchase.querySelector('[data-mobile-sticky-slot="buy"]'),
    };

    this.mobileStickyItems = [...this.querySelectorAll('[data-mobile-sticky-source]')]
      .map((element) => {
        const slot = slots[element.dataset.mobileStickySource];
        if (!slot) return null;
        const marker = document.createComment(`mobile-sticky-${element.dataset.mobileStickySource}`);
        element.before(marker);
        return { element, marker, slot };
      })
      .filter(Boolean);
    const mobileStickyOrder = { title: 0, price: 1, variants: 2, buy: 3 };
    this.mobileStickyItems.sort((first, second) => (
      mobileStickyOrder[first.element.dataset.mobileStickySource]
      - mobileStickyOrder[second.element.dataset.mobileStickySource]
    ));

    if (!this.mobileStickyItems.some((item) => item.element.dataset.mobileStickySource === 'buy')) return;

    this.mobileStickyMediaQuery = window.matchMedia('(max-width: 850px)');
    this.mobileStickyMediaHandler = () => this.updateMobileStickyPurchase();
    this.mobileStickyMediaQuery.addEventListener('change', this.mobileStickyMediaHandler);
    this.mobileStickyResizeObserver = new ResizeObserver(() => this.updateMobileStickyHeight());
    this.mobileStickyResizeObserver.observe(this.mobileStickyPurchase);
    this.updateMobileStickyPurchase();
  }

  updateMobileStickyPurchase() {
    if (!this.mobileStickyPurchase || !this.mobileStickyItems) return;
    if (!this.mobileStickyMediaQuery.matches) {
      this.restoreMobileStickyPurchase();
      return;
    }

    this.mobileStickyItems.forEach(({ element, slot }) => slot.append(element));
    this.mobileStickyPurchase.hidden = false;
    document.body.classList.add('lib-mobile-product-sticky-active');
    requestAnimationFrame(() => this.updateMobileStickyHeight());
  }

  restoreMobileStickyPurchase() {
    if (!this.mobileStickyPurchase || !this.mobileStickyItems) return;
    this.mobileStickyItems.forEach(({ element, marker }) => {
      marker.parentNode?.insertBefore(element, marker.nextSibling);
    });
    this.mobileStickyPurchase.hidden = true;
    document.body.classList.remove('lib-mobile-product-sticky-active');
    document.documentElement.style.removeProperty('--lib-mobile-product-sticky-height');
  }

  updateMobileStickyHeight() {
    if (!this.mobileStickyPurchase || this.mobileStickyPurchase.hidden) return;
    const height = Math.ceil(this.mobileStickyPurchase.getBoundingClientRect().height);
    document.documentElement.style.setProperty('--lib-mobile-product-sticky-height', `${height}px`);
  }

  openSizeGuide(trigger) {
    const dialog = this.querySelector('[data-size-guide-dialog]');
    if (!dialog) return;
    this.sizeGuideTrigger = trigger;
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
  }

  closeSizeGuide() {
    const dialog = this.querySelector('[data-size-guide-dialog]');
    if (!dialog) return;
    if (typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
      this.sizeGuideTrigger?.focus();
    }
  }

  initializeBackInStock() {
    const dialog = this.querySelector('[data-back-in-stock-dialog]');
    if (!dialog) return;
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) this.closeBackInStock();
    });
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.closeBackInStock();
    });
    dialog.addEventListener('close', () => {
      this.backInStockTrigger?.focus({ preventScroll: true });
    });
  }

  openBackInStock(trigger) {
    const dialog = this.querySelector('[data-back-in-stock-dialog]');
    const selectedVariantId = this.querySelector('.lib-product-form [name="id"]')?.value;
    if (!dialog || !selectedVariantId) return;

    this.backInStockTrigger = trigger;
    const variantInput = dialog.querySelector('[data-back-in-stock-variant-input]');
    const emailInput = dialog.querySelector('[data-back-in-stock-email]');
    const submitButton = dialog.querySelector('[data-back-in-stock-submit]');
    const status = dialog.querySelector('[data-back-in-stock-status]');
    if (variantInput) variantInput.value = selectedVariantId;
    if (emailInput) emailInput.disabled = false;
    if (submitButton) {
      submitButton.disabled = false;
      submitButton.removeAttribute('aria-busy');
    }
    if (status) {
      status.textContent = '';
      status.classList.remove('is-error', 'is-success');
    }

    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    requestAnimationFrame(() => emailInput?.focus());
  }

  closeBackInStock() {
    const dialog = this.querySelector('[data-back-in-stock-dialog]');
    if (!dialog) return;
    if (typeof dialog.close === 'function') dialog.close();
    else {
      dialog.removeAttribute('open');
      this.backInStockTrigger?.focus({ preventScroll: true });
    }
  }

  async submitBackInStock(form) {
    const dialog = form.closest('[data-back-in-stock-dialog]');
    const emailInput = form.querySelector('[data-back-in-stock-email]');
    const variantInput = form.querySelector('[data-back-in-stock-variant-input]');
    const submitButton = form.querySelector('[data-back-in-stock-submit]');
    const status = form.querySelector('[data-back-in-stock-status]');
    if (!dialog || !emailInput || !variantInput || !submitButton || !status) return;
    if (submitButton.disabled || this.backInStockSubmitting) return;
    if (!emailInput.checkValidity()) {
      emailInput.reportValidity();
      return;
    }

    status.textContent = '';
    status.classList.remove('is-error', 'is-success');
    submitButton.disabled = true;
    submitButton.setAttribute('aria-busy', 'true');
    this.backInStockSubmitting = true;

    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: { data: { type: 'profile', attributes: { email: emailInput.value.trim() } } },
          channels: ['EMAIL'],
        },
        relationships: {
          variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantInput.value}` } },
        },
      },
    };

    try {
      const companyId = encodeURIComponent(dialog.dataset.backInStockApiKey);
      const response = await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${companyId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          revision: '2026-07-15',
        },
        body: JSON.stringify(payload),
      });
      // Klaviyo accepts the subscription with HTTP 202 and an empty response body.
      if (!response.ok) throw new Error('Subscription failed');
      status.textContent = status.dataset.successLabel;
      status.classList.add('is-success');
      emailInput.disabled = true;
    } catch {
      status.textContent = status.dataset.errorLabel;
      status.classList.add('is-error');
      submitButton.disabled = false;
    } finally {
      this.backInStockSubmitting = false;
      submitButton.removeAttribute('aria-busy');
    }
  }

  openDescription() {
    if (window.matchMedia('(min-width: 851px)').matches) {
      const tab = this.querySelector('[data-description-tab]');
      if (!tab) return;
      this.activateProductTab(tab, false);
      tab.closest('[data-product-tabs]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    const details = this.querySelector('[data-description-details]');
    if (!details) return;
    details.open = true;
    details.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  activateProductTab(tab, focus = true) {
    const tabs = tab.closest('[data-product-tabs]');
    if (!tabs) return;
    const panelId = tab.getAttribute('aria-controls');
    tabs.querySelectorAll('[data-product-tab]').forEach((button) => {
      const active = button === tab;
      button.setAttribute('aria-selected', String(active));
      button.tabIndex = active ? 0 : -1;
    });
    tabs.querySelectorAll('[role="tabpanel"]').forEach((panel) => {
      panel.hidden = panel.id !== panelId;
    });
    if (focus) tab.focus();
  }

  handleProductTabKeydown(event) {
    const tab = event.target.closest('[data-product-tab]');
    if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const tabList = [...tab.closest('[role="tablist"]').querySelectorAll('[data-product-tab]')];
    const currentIndex = tabList.indexOf(tab);
    let nextIndex = currentIndex;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = tabList.length - 1;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabList.length) % tabList.length;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabList.length;
    event.preventDefault();
    this.activateProductTab(tabList[nextIndex]);
  }

  setSizeUnit(unit) {
    const dialog = this.querySelector('[data-size-guide-dialog]');
    if (!dialog) return;
    dialog.querySelectorAll('[data-size-unit]').forEach((button) => {
      const active = button.dataset.sizeUnit === unit;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
    dialog.querySelectorAll('[data-size-measure]').forEach((cell) => {
      if (unit === 'cm') {
        cell.textContent = cell.dataset.cm;
        return;
      }
      cell.textContent = cell.dataset.cm
        .split(/\s*[-–—]\s*/)
        .map((value) => {
          const measurement = Number.parseFloat(value.replace(',', '.'));
          return Number.isFinite(measurement) ? (measurement / 2.54).toFixed(2) : value;
        })
        .join(' - ');
    });
  }

  updateVariant() {
    this.updateOptionAvailability();
    const values = [...this.querySelectorAll('.lib-variants fieldset')].map((group) => group.querySelector('input:checked')?.value);
    const variant = this.variants.find((item) => item.options.every((value, index) => value === values[index]));
    const form = this.querySelector('.lib-product-form');
    const idInput = form?.querySelector('[name="id"]');
    const button = form?.querySelector('[data-add-to-cart]');
    if (!idInput || !button) return;
    if (!variant) {
      idInput.value = '';
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.querySelector('span').textContent = button.dataset.soldLabel;
      this.updateStock(null);
      this.updatePayPalMessage(null);
      this.updatePurchaseAvailability(null);
      return;
    }
    idInput.value = variant.id;
    button.disabled = !variant.available;
    button.setAttribute('aria-disabled', String(!variant.available));
    button.querySelector('span').textContent = variant.available ? button.dataset.availableLabel : button.dataset.soldLabel;
    this.updatePrice(variant);
    this.updatePayPalMessage(variant);
    this.updateStock(variant);
    this.updatePurchaseAvailability(variant);
    if (variant.featured_media) this.showMedia(variant.featured_media.id);
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  updatePurchaseAvailability(variant) {
    const formContent = this.querySelector('[data-purchase-form-content]');
    const trigger = this.querySelector('[data-back-in-stock-open]');
    const variantInput = this.querySelector('[data-back-in-stock-variant-input]');
    const isReferencePreview = this.dataset.referencePreview === 'true';
    const showBackInStock = Boolean(trigger && variant && !variant.available && !isReferencePreview);
    if (formContent) formContent.hidden = showBackInStock;
    if (trigger) {
      trigger.hidden = !showBackInStock;
      trigger.dataset.backInStockVariant = variant?.id || '';
    }
    if (variantInput) variantInput.value = variant?.id || '';
    if (!showBackInStock) {
      const dialog = this.querySelector('[data-back-in-stock-dialog]');
      if (dialog?.open) this.closeBackInStock();
    }
  }

  updatePrice(variant) {
    const price = this.querySelector('[data-product-price]');
    const compare = this.querySelector('[data-compare-price]');
    const isDiscounted = variant.compare_at_price > variant.price;
    price.textContent = this.formatMoney(variant.price);
    price.classList.toggle('lib-price--discounted', isDiscounted);
    compare.hidden = !isDiscounted;
    compare.textContent = variant.compare_at_price ? this.formatMoney(variant.compare_at_price) : '';
  }

  updatePayPalMessage(variant) {
    this.querySelectorAll('[data-pp-message]').forEach((message) => {
      if (!variant) {
        message.removeAttribute('data-pp-amount');
        return;
      }
      message.setAttribute('data-pp-amount', (Number(variant.price) / 100).toFixed(2));
    });
  }

  updateOptionAvailability() {
    const groups = [...this.querySelectorAll('.lib-variants fieldset')];
    if (groups.length === 0) return;
    const selectedValues = groups.map((group) => group.querySelector('input:checked')?.value);

    groups.forEach((group, optionIndex) => {
      group.querySelectorAll('input[type="radio"]').forEach((input) => {
        const available = this.variants.some((variant) => (
          variant.available
          && variant.options.every((value, index) => (
            index === optionIndex
              ? value === input.value
              : index > optionIndex || !selectedValues[index] || value === selectedValues[index]
          ))
        ));
        const label = input.nextElementSibling;
        if (!(label instanceof HTMLLabelElement)) return;
        input.dataset.optionAvailable = String(available);
        label.classList.toggle('is-unavailable', !available);
        const icon = label.querySelector('[data-variant-sold-out-icon]');
        const text = label.querySelector('[data-variant-sold-out-text]');
        if (icon) icon.hidden = available;
        if (text) text.hidden = available;
      });
    });
  }

  updateStock(variant) {
    const stock = this.querySelector('[data-product-stock]');
    if (!stock) return;
    const inventory = variant ? this.inventoryByVariant.get(String(variant.id)) : null;
    const threshold = Number.parseInt(stock.dataset.lowStockThreshold, 10);
    const isLowStock = Boolean(
      variant?.available
      && inventory?.inventory_management === 'shopify'
      && Number(inventory.inventory_quantity) > 0
      && Number(inventory.inventory_quantity) < threshold
    );
    stock.classList.toggle('is-unavailable', !variant?.available);
    stock.classList.toggle('is-low-stock', isLowStock);
    stock.querySelector('b').textContent = !variant?.available
      ? stock.dataset.soldLabel
      : isLowStock
        ? stock.dataset.lowStockLabel
        : stock.dataset.availableLabel;
  }

  changeQuantity(direction) {
    const input = this.querySelector('[data-quantity-input]');
    if (!input) return;
    this.normalizeQuantity();
    if (direction === 'increase') {
      input.stepUp();
    } else {
      input.stepDown();
    }
    this.syncQuantityButtons();
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  normalizeQuantity() {
    const input = this.querySelector('[data-quantity-input]');
    if (!input) return;
    const minimum = Number(input.min) || 1;
    const maximum = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
    const value = Number.parseInt(input.value, 10);
    input.value = String(Math.min(maximum, Math.max(minimum, Number.isFinite(value) ? value : minimum)));
    this.syncQuantityButtons();
  }

  syncQuantityButtons() {
    const input = this.querySelector('[data-quantity-input]');
    const decrease = this.querySelector('[data-quantity-change="decrease"]');
    const increase = this.querySelector('[data-quantity-change="increase"]');
    if (!input) return;
    if (decrease) decrease.disabled = Number(input.value) <= (Number(input.min) || 1);
    if (increase) increase.disabled = input.max !== '' && Number(input.value) >= Number(input.max);
  }

  showMedia(id) {
    const gallery = this.querySelector('[data-media-gallery]');
    const targetMedia = gallery?.querySelector(`[data-media-id="${CSS.escape(String(id))}"]`);
    const isMobile = window.matchMedia('(max-width: 850px)').matches;
    const isSlider = gallery?.dataset.galleryLayout === 'slider';

    if (isMobile && targetMedia) {
      gallery.querySelector('[data-media-stage]')?.scrollTo({ left: targetMedia.offsetLeft, behavior: 'smooth' });
    } else if (isSlider) {
      gallery?.querySelectorAll('[data-media-id]').forEach((media) => media.classList.toggle('is-hidden', media.dataset.mediaId !== String(id)));
    } else if (targetMedia) {
      targetMedia.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    this.setActiveMediaControl(id);
  }

  initializeGallery() {
    const gallery = this.querySelector('[data-media-gallery]');
    const stage = gallery?.querySelector('[data-media-stage]');
    if (!stage) return;

    const initialMedia = stage.querySelector('[data-media-id]:not(.is-hidden)')
      || stage.querySelector('[data-media-id]');
    if (initialMedia) this.updateGalleryAspectRatio(initialMedia.dataset.mediaId);

    let frame;
    stage.addEventListener('scroll', () => {
      if (!window.matchMedia('(max-width: 850px)').matches) return;
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const center = stage.scrollLeft + stage.clientWidth / 2;
        const media = [...stage.querySelectorAll('[data-media-id]')].reduce((closest, item) => {
          const itemCenter = item.offsetLeft + item.offsetWidth / 2;
          if (!closest) return item;
          const closestCenter = closest.offsetLeft + closest.offsetWidth / 2;
          return Math.abs(itemCenter - center) < Math.abs(closestCenter - center) ? item : closest;
        }, null);
        if (media) this.setActiveMediaControl(media.dataset.mediaId);
      });
    }, { passive: true });
  }

  setActiveMediaControl(id) {
    this.updateGalleryAspectRatio(id);
    this.querySelectorAll('[data-media-target]').forEach((button) => {
      const active = button.dataset.mediaTarget === String(id);
      button.classList.toggle('is-active', active);
      if (active) {
        button.setAttribute('aria-current', 'true');
        if (window.matchMedia('(max-width: 850px)').matches) {
          const controls = button.parentElement;
          controls?.scrollTo({
            left: button.offsetLeft - controls.clientWidth / 2 + button.offsetWidth / 2,
            behavior: 'smooth'
          });
        }
      } else {
        button.removeAttribute('aria-current');
      }
    });
  }

  updateGalleryAspectRatio(id) {
    const gallery = this.querySelector('[data-media-gallery]');
    const media = gallery?.querySelector(`[data-media-id="${CSS.escape(String(id))}"]`);
    const ratio = Number.parseFloat(media?.dataset.mediaAspectRatio);
    if (!gallery || !Number.isFinite(ratio) || ratio <= 0) return;
    gallery.style.setProperty('--gallery-active-aspect-ratio', String(ratio));
  }

  getPhotoSwipeItems() {
    return [...this.querySelectorAll('[data-media-gallery] [data-pswp-src]')].map((media) => {
      const image = media.querySelector('.lib-gallery__image-trigger img');
      const trigger = media.querySelector('.lib-gallery__image-trigger');
      return {
        src: media.dataset.pswpSrc,
        width: Number(media.dataset.pswpWidth),
        height: Number(media.dataset.pswpHeight),
        alt: media.dataset.pswpAlt || '',
        msrc: image?.currentSrc || image?.src || '',
        srcset: image?.srcset || '',
        element: trigger,
      };
    });
  }

  getPhotoSwipeModuleUrl(moduleUrl) {
    const url = new URL(moduleUrl, document.baseURI);
    if (url.hostname === 'cdn.shopify.com' && url.protocol === 'http:') url.protocol = 'https:';
    return url.href;
  }

  initPhotoSwipe() {
    if (this.photoSwipe) return Promise.resolve(this.photoSwipe);
    if (this.photoSwipePromise) return this.photoSwipePromise;

    const gallery = this.querySelector('[data-media-gallery][data-gallery-zoom-enabled="true"]');
    if (!gallery?.dataset.photoswipeLightboxUrl || !gallery.dataset.photoswipeCoreUrl) return Promise.resolve(null);

    this.photoSwipePromise = import(this.getPhotoSwipeModuleUrl(gallery.dataset.photoswipeLightboxUrl))
      .then(({ default: PhotoSwipeLightbox }) => {
        if (!this.isConnected) return null;
        const lightbox = new PhotoSwipeLightbox({
          dataSource: this.getPhotoSwipeItems(),
          pswpModule: () => import(this.getPhotoSwipeModuleUrl(gallery.dataset.photoswipeCoreUrl)),
          mainClass: 'libertas-photoswipe',
          bgOpacity: 0.96,
          spacing: 0.08,
          wheelToZoom: true,
          pinchToClose: true,
          closeOnVerticalDrag: true,
          imageClickAction: 'zoom-or-close',
          bgClickAction: 'close',
          tapAction: 'toggle-controls',
          doubleTapAction: 'zoom',
          returnFocus: true,
          trapFocus: true,
          preload: [1, 2],
          closeTitle: gallery.dataset.photoswipeCloseLabel,
          zoomTitle: gallery.dataset.photoswipeZoomLabel,
          arrowPrevTitle: gallery.dataset.photoswipePreviousLabel,
          arrowNextTitle: gallery.dataset.photoswipeNextLabel,
          errorMsg: gallery.dataset.photoswipeErrorLabel,
          indexIndicatorSep: ' / ',
          paddingFn: (viewportSize) => ({
            top: viewportSize.x < 750 ? 58 : 72,
            bottom: viewportSize.x < 750 ? 10 : 24,
            left: viewportSize.x < 750 ? 8 : 24,
            right: viewportSize.x < 750 ? 8 : 24,
          }),
        });
        lightbox.on('afterInit', () => {
          lightbox.pswp?.element?.setAttribute('aria-label', gallery.dataset.photoswipeGalleryLabel);
        });
        lightbox.on('openingAnimationEnd', () => {
          lightbox.pswp?.element?.setAttribute('data-photoswipe-ready', 'true');
        });
        lightbox.init();
        this.photoSwipe = lightbox;
        return lightbox;
      })
      .catch(() => {
        this.photoSwipePromise = null;
        return null;
      });

    return this.photoSwipePromise;
  }

  async openGalleryZoom(trigger) {
    const media = trigger.closest('[data-pswp-src]');
    if (!media) return;
    const imageMedia = [...this.querySelectorAll('[data-media-gallery] [data-pswp-src]')];
    const index = imageMedia.indexOf(media);
    if (index < 0) return;
    const lightbox = await this.initPhotoSwipe();
    lightbox?.loadAndOpen(index);
  }

  formatMoney(cents) {
    const hasFraction = Math.abs(cents) % 100 !== 0;
    return new Intl.NumberFormat(document.documentElement.lang || 'es', {
      style: 'currency',
      currency: this.dataset.currency,
      minimumFractionDigits: hasFraction ? 2 : 0,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  }
}

class LibertasDeliveryEstimate extends HTMLElement {
  connectedCallback() {
    if (this.dataset.deliveryReady === 'true') return;
    this.dataset.deliveryReady = 'true';
    this.updateEstimate();
    this.deliveryTimer = window.setInterval(() => this.updateEstimate(), 60000);
  }

  disconnectedCallback() {
    window.clearInterval(this.deliveryTimer);
    delete this.dataset.deliveryReady;
  }

  getCurrentParts() {
    const options = {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hourCycle: 'h23',
    };
    const timeZone = this.dataset.timeZone?.trim();
    if (timeZone) options.timeZone = timeZone;

    let formatter;
    try {
      formatter = new Intl.DateTimeFormat('en-CA', options);
    } catch {
      delete options.timeZone;
      formatter = new Intl.DateTimeFormat('en-CA', options);
    }

    return Object.fromEntries(
      formatter
        .formatToParts(new Date())
        .filter((part) => part.type !== 'literal')
        .map((part) => [part.type, Number.parseInt(part.value, 10)])
    );
  }

  addDeliveryDays(date, days, businessDaysOnly) {
    const result = new Date(date);
    let elapsed = 0;

    while (elapsed < days) {
      result.setUTCDate(result.getUTCDate() + 1);
      const weekday = result.getUTCDay();
      if (!businessDaysOnly || (weekday !== 0 && weekday !== 6)) elapsed += 1;
    }

    if (businessDaysOnly && days === 0) {
      while (result.getUTCDay() === 0 || result.getUTCDay() === 6) {
        result.setUTCDate(result.getUTCDate() + 1);
      }
    }

    return result;
  }

  formatDeliveryDate(date) {
    return new Intl.DateTimeFormat(document.documentElement.lang || 'es', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      timeZone: 'UTC',
    }).format(date);
  }

  updateEstimate() {
    const output = this.querySelector('[data-delivery-window]');
    if (!output) return;

    const current = this.getCurrentParts();
    const firstSetting = Math.max(0, Number.parseInt(this.dataset.minimumDays, 10) || 0);
    const lastSetting = Math.max(0, Number.parseInt(this.dataset.maximumDays, 10) || 0);
    const minimumDays = Math.min(firstSetting, lastSetting);
    const maximumDays = Math.max(firstSetting, lastSetting);
    const cutoffHour = Math.min(23, Math.max(0, Number.parseInt(this.dataset.cutoffHour, 10) || 0));
    const afterCutoff = this.dataset.cutoffEnabled === 'true' && current.hour >= cutoffHour;
    const cutoffShift = afterCutoff ? 1 : 0;
    const businessDaysOnly = this.dataset.businessDays === 'true';
    const today = new Date(Date.UTC(current.year, current.month - 1, current.day, 12));
    const firstDate = this.addDeliveryDays(today, minimumDays + cutoffShift, businessDaysOnly);
    const lastDate = this.addDeliveryDays(today, maximumDays + cutoffShift, businessDaysOnly);
    const firstLabel = this.formatDeliveryDate(firstDate);
    const lastLabel = this.formatDeliveryDate(lastDate);
    const dateWindow = firstDate.getTime() === lastDate.getTime()
      ? `${this.dataset.onLabel} ${firstLabel}`
      : `${this.dataset.betweenLabel} ${firstLabel} ${this.dataset.andLabel} ${lastLabel}`;

    output.textContent = [this.dataset.prefix, dateWindow].filter(Boolean).join(' ');

    const cutoffTime = this.querySelector('[data-delivery-cutoff-time]');
    if (cutoffTime) {
      const formattedHour = `${String(cutoffHour).padStart(2, '0')}:00`;
      cutoffTime.dateTime = formattedHour;
      cutoffTime.textContent = formattedHour;
    }

    const afterCutoffNote = this.querySelector('[data-delivery-after-cutoff]');
    if (afterCutoffNote) afterCutoffNote.hidden = !afterCutoff;
  }
}

if (!customElements.get('libertas-product')) customElements.define('libertas-product', LibertasProduct);
if (!customElements.get('libertas-delivery-estimate')) {
  customElements.define('libertas-delivery-estimate', LibertasDeliveryEstimate);
}

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('libertas-product').forEach((element) => element.connectedCallback());
});
